import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  return (
    <div data-testid="terms-of-service-page" className="min-h-screen bg-[#0B0D10] text-[#A6AFBD]">
      <div className="max-w-[800px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
        <Link
          to="/"
          data-testid="terms-back-link"
          className="inline-flex items-center gap-2 text-sm text-[#A6AFBD]/60 hover:text-[#F2F4F7] transition-colors mb-12"
        >
          <ArrowLeft size={16} />
          Back to Home
        </Link>

        <h1 className="text-4xl sm:text-5xl font-semibold text-[#F2F4F7] leading-tight tracking-tight mb-2">
          Terms of Service
        </h1>
        <p className="text-base text-[#A6AFBD]/60 mb-4">ramongovea.com</p>
        <p className="text-sm text-[#A6AFBD]/40 mb-12">
          Effective Date: April 2026 | Last Updated: April 2026
        </p>

        <div className="space-y-10 text-base leading-relaxed">
          <p>
            These Terms of Service govern your access to and use of IP Builder's Academy, operated by Ramon Govea under Twin Flame Productions. By purchasing any course tier or accessing any course materials, you agree to these terms in full.
          </p>

          <Section num="1" title="Acceptance of Terms">
            <p>By purchasing access to IP Builder's Academy or using any part of this website, you confirm that you are at least 18 years of age, have read and understood these Terms, and agree to be bound by them. If you do not agree, do not use our services.</p>
          </Section>

          <Section num="2" title="Course Access & Delivery">
            <p>Upon successful payment, you will receive access instructions via email within 24 hours. Course content is delivered through Skool. You are responsible for maintaining your own login credentials and ensuring you have the technical requirements to access the platform.</p>
            <p className="mt-3">Access is granted to the individual purchaser only and is non-transferable.</p>
            <p className="mt-3">Foundation and Pro tier students receive access to all available modules immediately upon enrollment, with remaining modules released on a weekly schedule. VIP tier students receive all of the above plus a single 90-minute 1-on-1 pitch review session with Ramon Govea.</p>
          </Section>

          <Section num="3" title="Pricing & Payment">
            <p>All prices are listed in US dollars. Payment is processed securely through Stripe. By completing a purchase you authorize the charge to your selected payment method.</p>
            <p className="mt-3">Prices are subject to change at any time. Any price change will not affect purchases already completed.</p>
          </Section>

          <Section num="4" title="Refund Policy — 14-Day Guarantee">
            <p>We stand behind the quality of IP Builder's Academy. If you complete Modules 1-4, attempt the included workflows, and do not find the course valuable, you may request a full refund within 14 days of your purchase date.</p>
            <p className="mt-3">To request a refund, email <a href="mailto:support@ramongovea.com" className="text-[#3B82F6] hover:underline">support@ramongovea.com</a> with your order details. Refunds are processed within 5-7 business days back to your original payment method.</p>
            <p className="mt-3">You may keep all downloaded templates upon refund — no questions asked.</p>
            <p className="mt-3">The following are not eligible for refunds:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Requests submitted after the 14-day window</li>
              <li>VIP 1-on-1 sessions that have already been conducted</li>
              <li>Pro or VIP tier purchases where the Group Zoom Q&A sessions have been attended</li>
            </ul>
          </Section>

          <Section num="5" title="VIP 1-on-1 Session Policy">
            <p>VIP tier purchasers receive one 90-minute 1-on-1 pitch review session with Ramon Govea. The following conditions apply:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Sessions must be scheduled within 90 days of purchase</li>
              <li>Cancellations or reschedules require a minimum of 24 hours notice</li>
              <li>One reschedule per purchased session is permitted</li>
              <li>Cancellations with less than 24 hours notice or no-shows result in forfeiture of the session with no refund on the VIP tier difference</li>
              <li>Sessions are conducted via Zoom and are not recorded by either party without mutual written consent</li>
            </ul>
          </Section>

          <Section num="6" title="Pro Tier Group Q&A Sessions">
            <p>Pro and VIP tier students receive access to twice-monthly live 45-minute Group Zoom Q&A sessions with Ramon Govea. The following conditions apply:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Sessions are live only and are not recorded</li>
              <li>Missed sessions cannot be rescheduled or carried over</li>
              <li>Session schedules are posted in the Skool community in advance</li>
              <li>We reserve the right to reschedule sessions with reasonable notice to enrolled students</li>
            </ul>
          </Section>

          <Section num="7" title="Intellectual Property — Your Work">
            <p>Any IP, story concepts, characters, pitch materials, or creative works you develop using the frameworks, templates, or workflows taught in IP Builder's Academy belong entirely to you. Ramon Govea and Twin Flame Productions make no claim of ownership over any creative work produced by students of this course.</p>
            <p className="mt-4 font-medium text-[#F2F4F7]">Your ideas are yours. Full stop.</p>
          </Section>

          <Section num="8" title="Intellectual Property — Course Content">
            <p>All course content including video lessons, the Story Framework Formula, templates, written materials, and AI prompt packs are the intellectual property of Ramon Govea and Twin Flame Productions and are protected by copyright law.</p>
            <p className="mt-3">By purchasing the course you receive a personal, non-transferable license to access and use the materials for your own creative development. You may not:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Reproduce, redistribute, resell, or sublicense any course content</li>
              <li>Share login credentials or grant access to non-paying individuals</li>
              <li>Screen record, download, or duplicate video lessons</li>
              <li>Use course materials to create a competing course or educational product</li>
              <li>Publicly publish or share course materials in whole or in part without written permission</li>
            </ul>
            <p className="mt-3">Violation of these terms will result in immediate termination of access without refund and may result in legal action.</p>
          </Section>

          <Section num="9" title="Community Conduct & Confidentiality">
            <p>IP Builder's Academy includes access to a private Skool community and live Group Q&A sessions. These spaces are designed to be a protected creative environment where students can share works-in-progress, pitch concepts, and creative ideas without fear of theft or exploitation.</p>
            <p className="mt-3">By participating in any community space — including the Skool community, Group Q&A sessions, or any other forum associated with IP Builder's Academy — you agree to the following:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>You will not reproduce, share, pitch, develop, or exploit any creative idea, concept, character, story, or IP belonging to another student that was disclosed in any community space</li>
              <li>You will treat all student-shared creative work as confidential</li>
              <li>You will not record, screenshot, or otherwise capture other students' shared work without their explicit written consent</li>
              <li>You understand that violation of another student's creative confidentiality may expose you to legal liability under applicable intellectual property and unfair competition laws</li>
            </ul>
            <p className="mt-3">This confidentiality obligation survives termination of your course access and remains in effect indefinitely.</p>
            <p className="mt-3">We reserve the right to remove any student from the community or course without refund for violation of these terms.</p>
          </Section>

          <Section num="10" title="Prohibited Conduct">
            <p>In addition to the intellectual property and confidentiality restrictions above, you agree not to:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Use the course or community to harass, defame, or harm other students or instructors</li>
              <li>Engage in spam, solicitation, or self-promotion within the community without permission</li>
              <li>Misrepresent your identity or credentials within the community</li>
              <li>Use course content or community access for any unlawful purpose</li>
            </ul>
          </Section>

          <Section num="11" title="Disclaimers">
            <p>IP Builder's Academy provides education and frameworks based on Ramon Govea's professional experience. We do not guarantee specific outcomes including but not limited to sales, licensing deals, funding, or Hollywood representation.</p>
            <p className="mt-3">Results depend entirely on the individual student's effort, talent, market conditions, and factors outside our control. Testimonials and examples used are not guarantees of similar results.</p>
            <p className="mt-3">Course content reflects the instructor's professional experience and perspective and is not legal, financial, or business advice. Consult qualified professionals for advice specific to your situation.</p>
          </Section>

          <Section num="12" title="Limitation of Liability">
            <p>To the fullest extent permitted by law, Ramon Govea and Twin Flame Productions shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of or inability to use this course or its materials. Our total liability to you for any claim arising from these Terms shall not exceed the amount you paid for your course tier.</p>
          </Section>

          <Section num="13" title="Governing Law">
            <p>These Terms of Service are governed by and construed in accordance with the laws of the State of Florida, without regard to conflict of law principles. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts located in Miami-Dade County, Florida.</p>
          </Section>

          <Section num="14" title="Changes to These Terms">
            <p>We reserve the right to update these Terms at any time. Changes will be posted to this page with an updated effective date. Continued use of the course or community after any changes constitutes your acceptance of the updated Terms.</p>
          </Section>

          <Section num="15" title="Contact">
            <p>For any questions regarding these Terms of Service, contact us at:</p>
            <p className="mt-2">
              <a href="mailto:support@ramongovea.com" className="text-[#3B82F6] hover:underline">support@ramongovea.com</a>
            </p>
            <p className="mt-3">We typically respond within 2 business days.</p>
          </Section>
        </div>
      </div>
    </div>
  );
};

const Section = ({ num, title, children }) => (
  <div>
    <h2 className="text-lg font-semibold text-[#F2F4F7] mb-3">
      {num}. {title}
    </h2>
    <div className="space-y-3">{children}</div>
  </div>
);

export default TermsOfService;
